import React, { useState, useEffect } from "react";
import {
  FileText, Clock, Calendar, DollarSign,
  Search, X, Shield, MessageCircle,
  CheckCircle, AlertCircle, Filter,
  List, Grid, Star, User, Package,
  MoreHorizontal, ChevronDown, Receipt,
  FileCheck, Timer, Briefcase, MapPin,
  Send, Edit, Trash2, Check, Loader
} from "lucide-react";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/Button";
import { Badge } from "../components/ui/badge";
import ProfessionalInviteModal from "../components/ui/ProfessionalInviteModal";
import SharedHeader5 from "../Headers/SharedHeader5";
import SharedFooter3 from "../Footer/SharedFooter3";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getUserJobs, updateUserJob, deleteUserJob, useAppDispatch } from "../redux";
import { JobStatusEnum, TypeEnum } from "../modules/shared/model";
import ActiveContractsTab from "./components/ActiveContractsTab";
import { fetchContracts } from "../redux/Slices/contractSlice";
import { ContractStatusEnum } from "../modules";
import { getDynamicCurrencySymbol } from "../lib/currencyUtils";
import { useTranslation } from "react-i18next";
import { getTranslatedCategoryName } from "../utils/categoryTranslation";

// Add custom CSS for hiding scrollbars
const customStyles = `
  .scrollbar-hide::-webkit-scrollbar {
    display: none;
  }
  .scrollbar-hide {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
`;

const MyOrdersPage = () => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language.split("-")[0];
  // Redux hooks
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useSelector((state: any) => state.auth);
  const [jobs, setJobs] = useState([]);

  // Pagination state for quote requests
  const [quotePagination, setQuotePagination] = useState({
    currentPage: 1,
    limit: 10,
    totalCount: 0,
    totalPages: 0,
    hasNextPage: false
  });

  // State management
  const [selectedTab, setSelectedTab] = useState(() => {
    // Get saved tab from localStorage, default to "requests"
    const savedTab = localStorage.getItem("myOrdersSelectedTab");
    return savedTab || "requests";
  });
  const [viewMode, setViewMode] = useState(() => {
    // Get saved view mode from localStorage, default to "list"
    const savedViewMode = localStorage.getItem("myOrdersViewMode");
    return savedViewMode || "list";
  });
  const [activeContractsCount, setActiveContractsCount] = useState(0);
  const [pastOrdersCount, setPastOrdersCount] = useState(0);
  const [closedOrdersCount, setClosedOrdersCount] = useState(0);
  const [totalHiringPostsCount, setTotalHiringPostsCount] = useState(0);
  const [filterOpen, setFilterOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [countsLoading, setCountsLoading] = useState(false);
  const [countsInitialized, setCountsInitialized] = useState(false);

  const [filters, setFilters] = useState({
    orderType: "all",
    status: "all",
    timeframe: "all",
    category: "all",
    urgency: "all",
  });
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showJobDetailsModal, setShowJobDetailsModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [editFormData, setEditFormData] = useState({
    title: "",
    description: "",
    budget: "",
    address: "",
    city: "",
    province: "",
    postalCode: ""
  });
  const [showProfessionalInviteModal, setShowProfessionalInviteModal] = useState(false);

  // Fetch user jobs on component mount
  useEffect(() => {
    const getJobs = async () => {
      if (user?.id) {
        const jobs = await dispatch(getUserJobs({
          userId: user.id,
          page: 1,
          limit: 10,
          filters: {
            status: [JobStatusEnum.Active, JobStatusEnum.InActive, JobStatusEnum.Pending, JobStatusEnum.InProgress, JobStatusEnum.Approved, JobStatusEnum.Flagged, JobStatusEnum.Contracted]
            // Include all statuses to show inactive jobs too
          },
          sort: [{ orderBy: "createdAt", order: "DESC" }] // Sort by creation date descending (latest first)
        }));
        if (jobs.meta.requestStatus === "fulfilled") {
          setJobs(jobs.payload.data || jobs.payload);
        }
      }
    };
    getJobs();
  }, [dispatch, user?.id]);

  // Listen for custom event to switch to closed tab
  useEffect(() => {
    const handleSwitchToClosedTab = () => {
      console.log("Received switchToClosedTab event, switching to closed tab");
      setSelectedTab("closed");
      // Refresh counts if needed
      if (!countsInitialized) {
        fetchAllCounts(true);
      }
    };

    window.addEventListener("switchToClosedTab", ()=>{
      setCountsInitialized(false);
      handleSwitchToClosedTab();
    });

    return () => {
      window.removeEventListener("switchToClosedTab", handleSwitchToClosedTab);
    };
  }, [countsInitialized]);

  // Single function to fetch all counts with proper loading state management
  const fetchAllCounts = async (forceRefresh = false) => {
    if (!user?.id || (countsLoading && !forceRefresh)) return;
    
    setCountsLoading(true);
    try {
      // Fetch all counts in parallel but with proper error handling
      const [hiringPostsResponse, activeContractsResponse, pastOrdersResponse, closedOrdersResponse] = await Promise.allSettled([
        // Hiring posts count
        dispatch(getUserJobs({
          userId: user.id,
          page: 1,
          limit: 1,
          filters: {
            status: [JobStatusEnum.Active, JobStatusEnum.InActive, JobStatusEnum.Pending, JobStatusEnum.InProgress, JobStatusEnum.Approved, JobStatusEnum.Flagged, JobStatusEnum.Contracted]
          },
          sort: [{ orderBy: "createdAt", order: "DESC" }]
        })),
        // Active contracts count (all active statuses)
        dispatch(fetchContracts({ 
          page: 1, 
          limit: 1, 
          clientId: user.id,
          filters: JSON.stringify({ statusIds: [ContractStatusEnum.Active, ContractStatusEnum.Accepted] })
        } as any)),
        // Past orders count (completed contracts)
        dispatch(fetchContracts({ 
          page: 1, 
          limit: 1, 
          clientId: user.id, 
          filters: JSON.stringify({ statusIds: [ContractStatusEnum.Completed] })
        } as any)),
        // Closed orders count (closed contracts)
        dispatch(fetchContracts({ 
          page: 1, 
          limit: 1, 
          clientId: user.id, 
          filters: JSON.stringify({ statusIds: [ContractStatusEnum.Closed] })
        } as any))
      ]);

      // Update counts based on successful responses
      if (hiringPostsResponse.status === "fulfilled" && hiringPostsResponse.value?.meta?.requestStatus === "fulfilled") {
        const totalCount = hiringPostsResponse.value.payload?.totalCount || 0;
        setTotalHiringPostsCount(Number(totalCount) || 0);
      }

      if (activeContractsResponse.status === "fulfilled" && activeContractsResponse.value?.meta?.requestStatus === "fulfilled") {
        const payload = activeContractsResponse.value.payload as any;
        const totalCount = payload?.totalCount || 0;
        setActiveContractsCount(Number(totalCount) || 0);
      }

      if (pastOrdersResponse.status === "fulfilled" && pastOrdersResponse.value?.meta?.requestStatus === "fulfilled") {
        const payload = pastOrdersResponse.value.payload as any;
        const totalCount = payload?.totalCount || 0;
        setPastOrdersCount(Number(totalCount) || 0);
      }

      if (closedOrdersResponse.status === "fulfilled" && closedOrdersResponse.value?.meta?.requestStatus === "fulfilled") {
        const payload = closedOrdersResponse.value.payload as any;
        const totalCount = payload?.totalCount || 0;
        setClosedOrdersCount(Number(totalCount) || 0);
      }

      setCountsInitialized(true);
    } catch (error) {
      console.error("Error fetching counts:", error);
    } finally {
      setCountsLoading(false);
    }
  };

  // Load counts on component mount
  useEffect(() => {
    if (user?.id && !countsInitialized) {
      fetchAllCounts();
    }
  }, [dispatch, user?.id, countsInitialized]);

  // Show professional invite modal if isJobCreated query parameter is true
  useEffect(() => {
    const isJobCreated = searchParams.get("isJobCreated");
    console.log("isJobCreated", isJobCreated);
    if (isJobCreated === "true") {
      setTimeout(() => {
        setShowProfessionalInviteModal(true);
        // Remove the query parameter from URL after showing the modal
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete("isJobCreated");
        setSearchParams(newSearchParams, { replace: true });
      }, 3000);

    }
  }, [searchParams, setSearchParams]);

  // Fetch quote requests with pagination and filters
  const fetchQuoteRequests = async (page = 1, limit = 10, additionalFilters: any = {}) => {
    if (user?.id) {
      setLoading(true);
      try {
        // Combine all filters
        const allFilters = { ...{ status: [JobStatusEnum.Active, JobStatusEnum.InActive, JobStatusEnum.Pending, JobStatusEnum.InProgress, JobStatusEnum.Approved, JobStatusEnum.Flagged, JobStatusEnum.Contracted] }, ...additionalFilters };

        const response = await dispatch(getUserJobs({
          userId: user.id,
          page,
          limit,
          filters: allFilters,
          search: searchQuery,
          sort: [{ orderBy: "createdAt", order: "DESC" }] // Sort by creation date descending (latest first)
        }));

        if (response.meta.requestStatus === "fulfilled") {
          const data = response.payload;
          setJobs(data.data || data);

          // Update pagination state
          const totalCount = data.totalCount || 0;
          const totalPages = Math.ceil(totalCount / limit);
          setQuotePagination(prev => ({
            ...prev,
            currentPage: page,
            totalCount,
            totalPages,
            hasNextPage: page < totalPages
          }));
        }
      } catch (error) {
      } finally {
        setLoading(false);
      }
    }
  };

  // Handle page change for quote requests
  const handleQuotePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= quotePagination.totalPages) {
      fetchQuoteRequests(newPage, quotePagination.limit);
    }
  };

  // Fetch quote requests when tab changes to requests
  useEffect(() => {
    if (selectedTab === "requests") {
      fetchQuoteRequests(1, quotePagination.limit);
    }
  }, [selectedTab, user?.id]);

  // Debounced search effect
  useEffect(() => {
    if (selectedTab === "requests") {
      const timeoutId = setTimeout(() => {
        fetchQuoteRequests(1, quotePagination.limit);
      }, 500);

      return () => clearTimeout(timeoutId);
    }
  }, [searchQuery, selectedTab]);

  // Transform jobs data to match the expected format for Request Quotes
  const transformJobsToQuotes = (jobsData) => {
    if (!jobsData || !Array.isArray(jobsData)) return [];

    return jobsData.map(job => ({
      id: job.id,
      serviceType: job.categoryId ? (getTranslatedCategoryName(job.categoryId, currentLang) || job.categoryId.name) : "General Service",
      description: job.description || job.title,
      submittedDate: job.createdAt,
      status: getJobStatusName(job.status?.id), // Use proper status from backend
      responses: 0, // This would need to be fetched from a separate API if available
      details: {
        location: job.address ? `${job.address}, ${job.city}, ${job.province}` : null,
        budget: job.budget,
        isEmergency: job.isEmergency,
        isQuote: job.isQuote,
        category: job.categoryId ? (getTranslatedCategoryName(job.categoryId, currentLang) || job.categoryId.name) : null,
        title: job.title || "" // Show empty string if no title
      },
      // Add the original job data for reference
      originalJob: job
    }));
  };

  // Helper function to get floor suffix (1st, 2nd, 3rd, etc.)
  const getFloorSuffix = (floor: number) => {
    if (floor >= 11 && floor <= 13) return "th";
    switch (floor % 10) {
      case 1: return "st";
      case 2: return "nd";
      case 3: return "rd";
      default: return "th";
    }
  };

  // Helper functions for enum mapping
  const getServiceTypeName = (id: any) => {
    const numId = Number(id);
    switch (numId) {
      case 1: return t("myOrdersPage.assistance");
      case 2: return t("myOrdersPage.repair");
      case 3: return t("myOrdersPage.replacement");
      default: return t("myOrdersPage.notSpecified");
    }
  };

  const getPropertyTypeName = (id: any) => {
    const numId = Number(id);
    switch (numId) {
      case 1: return t("myOrdersPage.residential");
      case 2: return t("myOrdersPage.commercial");
      case 3: return t("myOrdersPage.industrial");
      default: return t("myOrdersPage.notSpecified");
    }
  };

  const getBudgetTypeName = (id: any) => {
    const numId = Number(id);
    switch (numId) {
      case 1: return t("myOrdersPage.fixedPriceBudget");
      case 2: return t("myOrdersPage.hourlyRateBudget");
      default: return t("myOrdersPage.notSpecified");
    }
  };

  const getDeadlineTypeName = (id: any) => {
    const numId = Number(id);
    switch (numId) {
      case 1: return t("myOrdersPage.informationOnly");
      case 2: return t("myOrdersPage.withinAWeek");
      case 3: return t("myOrdersPage.withinAMonth");
      case 4: return t("myOrdersPage.futureMonth");
      case 5: return t("myOrdersPage.isEmergency");
      default: return t("myOrdersPage.notSpecified");
    }
  };

  const getCurrencyName = (id: string) => {
    switch (id) {
      case "USD": return "US Dollar (USD)";
      case "CAD": return "Canadian Dollar (CAD)";
      case "GBP": return "British Pound (GBP)";
      case "EUR": return "Euro (EUR)";
      case "AUD": return "Australian Dollar (AUD)";
      case "JPY": return "Japanese Yen (JPY)";
      case "CHF": return "Swiss Franc (CHF)";
      case "CNY": return "Chinese Yuan (CNY)";
      case "INR": return "Indian Rupee (INR)";
      case "MXN": return "Mexican Peso (MXN)";
      default: return id || "US Dollar (USD)";
    }
  };

  const getJobStatusName = (id: any) => {
    const numId = Number(id);
    switch (numId) {
      case JobStatusEnum.Pending: return t("activeContractsTab.pending");
      case JobStatusEnum.InProgress: return t("myOrdersPage.inProgress");
      case JobStatusEnum.Completed: return t("activeContractsTab.completed");
      case JobStatusEnum.Cancelled: return t("activeContractsTab.cancelled");
      case JobStatusEnum.InActive: return "Inactive";
      case JobStatusEnum.Active: return t("activeContractsTab.active");
      case JobStatusEnum.Approved: return "Approved";
      case JobStatusEnum.Flagged: return "Flagged";
      case JobStatusEnum.Deleted: return t("activeContractsTab.deleted");
      case JobStatusEnum.Contracted: return "Contracted";
      default: return t("activeContractsTab.unknown");
    }
  };

  // Get transformed quote requests from jobs data (backend already filters out inactive jobs)
  const requestQuotes = transformJobsToQuotes(jobs);

  // Handle tab change with localStorage persistence
  const handleTabChange = (tab: string) => {
    setSelectedTab(tab);
    localStorage.setItem("myOrdersSelectedTab", tab);

    // Only refresh counts if not already initialized or if force refresh is needed
    if (!countsInitialized) {
      fetchAllCounts(true);
    }
  };

  // Handle view mode change with localStorage persistence
  const handleViewModeChange = (mode: string) => {
    setViewMode(mode);
    localStorage.setItem("myOrdersViewMode", mode);
  };

  const handleDeactivateJob = (job) => {
    setSelectedJob(job);
    setShowDeleteModal(true);
  };

  const handleActivateJob = async (job) => {
    try {
      // Update job status to Active (status 6) along with other fields
      await dispatch(updateUserJob({
        id: job.id,
        data: {
          status: { id: JobStatusEnum.Active }, // Active status
          emergencyDays: job.emergencyDays && job.emergencyDays?.id ? { id: Number(job.emergencyDays.id) } : undefined,
          emergencyTimeSlot: job.emergencyTimeSlot && job.emergencyTimeSlot?.id ? { id: Number(job.emergencyTimeSlot.id) } : undefined,
          equipmentType: job.equipmentType ? { id: Number(job.equipmentType.id) } : undefined,
          propertyType: job.propertyType ? { id: Number(job.propertyType.id) } : undefined,
          budgetType: job.budgetType ? { id: Number(job.budgetType.id) } : undefined,
          deadlineType: job.deadlineType ? { id: Number(job.deadlineType.id) } : undefined,
          serviceType: job.serviceType ? { id: Number(job.serviceType.id) } : undefined,
          durationTimeline: job.durationTimeline ? { id: Number(job.durationTimeline.id) || 1 } : undefined,
          requiredExperience: job.requiredExperience ? { id: Number(job.requiredExperience.id) || 1 } : undefined,
          files: job.files ? job.files : [],
          languages: job.languages ? job.languages : [],
          serviceArea: job.serviceArea || undefined,
        }
      })).unwrap();

      // Refresh the current page after status update
      if (selectedTab === "requests") {
        fetchQuoteRequests(quotePagination.currentPage, quotePagination.limit);
      }

      // Refresh total counts
      fetchAllCounts(true);
    } catch (error) {
    }
  };

  // Handle permanent deletion of job by updating status to 9 (deleted)
  const handlePermanentlyDeleteJob = async (job) => {
    try {
      // Show confirmation dialog
      if (!window.confirm(t("myOrdersPage.confirmPermanentDelete"))) {
        return;
      }

      // Update job status to deleted (status 9) along with other fields
      await dispatch(updateUserJob({
        id: job.id,
        data: {
          status: { id: JobStatusEnum.Deleted }, // Deleted status
          emergencyDays: job.emergencyDays && job.emergencyDays?.id ? { id: Number(job.emergencyDays.id) } : undefined,
          emergencyTimeSlot: job.emergencyTimeSlot && job.emergencyTimeSlot?.id ? { id: Number(job.emergencyTimeSlot.id) } : undefined,
          equipmentType: job.equipmentType ? { id: Number(job.equipmentType.id) } : undefined,
          propertyType: job.propertyType ? { id: Number(job.propertyType.id) } : undefined,
          budgetType: job.budgetType ? { id: Number(job.budgetType.id) } : undefined,
          deadlineType: job.deadlineType ? { id: Number(job.deadlineType.id) } : undefined,
          serviceType: job.serviceType ? { id: Number(job.serviceType.id) } : undefined,
          durationTimeline: job.durationTimeline ? { id: Number(job.durationTimeline.id) || 1 } : undefined,
          requiredExperience: job.requiredExperience ? { id: Number(job.requiredExperience.id) || 1 } : undefined,
          files: job.files ? job.files : [],
          languages: job.languages ? job.languages : [],
          serviceArea: job.serviceArea || undefined,
        }
      })).unwrap();

      // Show success message


      // Refresh the current page after deletion
      if (selectedTab === "requests") {
        fetchQuoteRequests(quotePagination.currentPage, quotePagination.limit);
      }

      // Refresh total counts
      fetchAllCounts(true);
    } catch (error) {
      alert(t("myOrdersPage.deleteJobError"));
    }
  };

  // Handle cancel order
  const handleCancelOrder = async (job) => {
    try {
      // Update job status to Cancelled (status 4) along with other fields
      await dispatch(updateUserJob({
        id: job.id,
        data: {
          status: { id: JobStatusEnum.Cancelled }, // Cancelled status
          emergencyDays: job.emergencyDays && job.emergencyDays?.id ? { id: Number(job.emergencyDays.id) } : undefined,
          emergencyTimeSlot: job.emergencyTimeSlot && job.emergencyTimeSlot?.id ? { id: Number(job.emergencyTimeSlot.id) } : undefined,
          equipmentType: job.equipmentType ? { id: Number(job.equipmentType.id) } : undefined,
          propertyType: job.propertyType ? { id: Number(job.propertyType.id) } : undefined,
          budgetType: job.budgetType ? { id: Number(job.budgetType.id) } : undefined,
          deadlineType: job.deadlineType ? { id: Number(job.deadlineType.id) } : undefined,
          serviceType: job.serviceType ? { id: Number(job.serviceType.id) } : undefined,
          durationTimeline: job.durationTimeline ? { id: Number(job.durationTimeline.id) || 1 } : undefined,
          requiredExperience: job.requiredExperience ? { id: Number(job.requiredExperience.id) || 1 } : undefined,
          files: job.files ? job.files : [],
          languages: job.languages ? job.languages : [],
          serviceArea: job.serviceArea || undefined
        }
      })).unwrap();

      // Refresh the current page after status update
      if (selectedTab === "requests") {
        fetchQuoteRequests(quotePagination.currentPage, quotePagination.limit);
      }

      // Refresh total counts
      fetchAllCounts(true);

      // Close the job details modal if open
      setShowJobDetailsModal(false);
    } catch (error) {
    }
  };

  const handleConfirmDelete = async (status: JobStatusEnum) => {
    try {

      // Update job status to InActive instead of deleting along with other fields
      await dispatch(updateUserJob({
        id: selectedJob.id,
        data: {
          status: { id: status }, // InActive status
          emergencyDays: selectedJob.emergencyDays && selectedJob.emergencyDays?.id ? { id: Number(selectedJob.emergencyDays.id) || 1 } : undefined,
          emergencyTimeSlot: selectedJob.emergencyTimeSlot && selectedJob.emergencyTimeSlot?.id ? { id: Number(selectedJob.emergencyTimeSlot.id) || 1 } : undefined,
          equipmentType: selectedJob.equipmentType ? { id: Number(selectedJob.equipmentType.id) || 1 } : undefined,
          propertyType: selectedJob.propertyType ? { id: Number(selectedJob.propertyType.id) || 1 } : undefined,
          budgetType: selectedJob.budgetType ? { id: Number(selectedJob.budgetType.id) || 1 } : undefined,
          deadlineType: selectedJob.deadlineType ? { id: Number(selectedJob.deadlineType.id) || 1 } : undefined,
          serviceType: selectedJob.serviceType ? { id: Number(selectedJob.serviceType.id) || 1 } : undefined,
          durationTimeline: selectedJob.durationTimeline ? { id: Number(selectedJob.durationTimeline.id) || 1 } : undefined,
          requiredExperience: selectedJob.requiredExperience ? { id: Number(selectedJob.requiredExperience.id) || 1 } : undefined,
          files: selectedJob.files ? selectedJob.files : [],
          languages: selectedJob.languages ? selectedJob.languages : [],
          serviceArea: selectedJob.serviceArea || undefined
        }
      })).unwrap();
      setShowDeleteModal(false);
      setSelectedJob(null);

      // Refresh the current page after status update
      if (selectedTab === "requests") {
        fetchQuoteRequests(quotePagination.currentPage, quotePagination.limit);
      }

      // Refresh total counts
      fetchAllCounts(true);
    } catch (error) {
    }
  };

  // Mock data for active orders
  const activeOrders = [
    {
      id: "ORD-2024-001",
      type: "local",
      serviceType: "Plumbing",
      description: "Kitchen sink repair and pipe replacement",
      amount: 250,
      currency: "USD",
      status: "scheduled",
      scheduledDate: "2024-01-22",
      timeSlot: "14:00-16:00",
      location: "Home Address",
      professional: {
        name: "Mike Wilson",
        rating: 4.9,
        reviews: 89,
        verified: true
      },
      emergency: false,
      lastUpdate: "2 hours ago",
      unreadMessages: 2
    },
    {
      id: "ORD-2024-002",
      type: "international",
      serviceType: "Web Development",
      description: "E-commerce website development with React and Node.js",
      amount: 3500,
      currency: "USD",
      status: "in-progress",
      startDate: "2024-01-15",
      endDate: "2024-02-28",
      progress: 45,
      deliverables: [
        { name: "Design Phase", completed: true },
        { name: "Frontend Development", completed: false },
        { name: "Backend Development", completed: false }
      ],
      professional: {
        name: "Sarah Chen",
        rating: 4.95,
        reviews: 156,
        verified: true
      },
      lastUpdate: "1 day ago"
    }
  ];

  // Memoize callbacks to prevent unnecessary remounting of ActiveContractsTab
  const handlePastOrdersCountChange = React.useCallback((count: number) => {
    if (!countsLoading) {
      setPastOrdersCount(Number(count) || 0);
    }
  }, [countsLoading]);

  const handleClosedOrdersCountChange = React.useCallback((count: number) => {
    if (!countsLoading) {
      setClosedOrdersCount(Number(count) || 0);
    }
  }, [countsLoading]);

  const handleContractCompleted = React.useCallback(() => {
    fetchAllCounts(true);
  }, []);


  // Order Card Component
  const OrderCard = ({ order }) => {
    const isInternational = order.type === "international";
    const isEmergency = order.emergency;

    return (
      <Card className="p-4 sm:p-6 hover:shadow-lg transition-shadow" style={{ textAlign: "left" }}>
        <div className="flex flex-col gap-4">
          {/* Order Header */}
          <div className="flex flex-col sm:flex-row justify-between gap-2">
            <div className="space-y-1">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="text-lg font-semibold text-gray-900">
                  {order.serviceType}
                </h3>
                {isEmergency && (
                  <Badge variant="destructive" className="bg-red-100 text-red-800">
                    Emergency
                  </Badge>
                )}
                <Badge variant={undefined} className="bg-blue-100 text-blue-800">
                  {order.type === "international" ? "International" : "Local"}
                </Badge>
              </div>
              <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600">
                <span>Order {order.id}</span>
                <span className="hidden sm:inline">•</span>
                <span>
                  {isInternational
                    ? `Due ${new Date(order.endDate).toLocaleDateString()}`
                    : `Scheduled for ${new Date(order.scheduledDate).toLocaleDateString()}`
                  }
                </span>
              </div>
            </div>
            <Button variant="ghost" size="sm" className="self-start">
              <MoreHorizontal className="h-5 w-5" />
            </Button>
          </div>

          {/* Description */}
          <p className="text-gray-600">
            {order.description && order.description.length > 200
              ? `${order.description.substring(0, 200)}...`
              : order.description}
          </p>

          {/* Progress Bar */}
          {order.status === "in-progress" && order.progress && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">Progress</span>
                <span className="text-sm text-gray-600">{order.progress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full"
                  style={{ width: `${order.progress}%` }}
                />
              </div>
            </div>
          )}

          {/* Professional Info */}
          <div className="flex items-center gap-4">
            <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
              <User className="h-6 w-6 text-gray-600" />
            </div>
            <div>
              <div className="font-medium">{order.professional.name}</div>
              <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600">
                <Star className="h-4 w-4 text-yellow-400 fill-current" />
                <span>{order.professional.rating}</span>
                <span>({order.professional.reviews} reviews)</span>
                {order.professional.verified && (
                  <Badge variant={undefined} className="bg-green-50 text-green-700">Verified</Badge>
                )}
              </div>
            </div>
          </div>

          {/* Order Details for Mobile */}
          <div className="sm:hidden space-y-4 border-t border-b py-4">
            <div>
              <div className="text-sm text-gray-600">Amount</div>
              <div className="text-lg font-semibold">
                {order.currency} {order.amount.toLocaleString()}
              </div>
            </div>

            <div>
              <div className="text-sm text-gray-600">
                {isInternational ? "Timeline" : "Appointment"}
              </div>
              <div className="font-medium">
                {isInternational ? (
                  <>
                    Start: {new Date(order.startDate).toLocaleDateString()}
                    <br />
                    End: {new Date(order.endDate).toLocaleDateString()}
                  </>
                ) : (
                  <>
                    {new Date(order.scheduledDate).toLocaleDateString()}
                    <br />
                    {order.timeSlot}
                  </>
                )}
              </div>
            </div>

            <div>
              <Badge variant={undefined}
                className={`
                  ${order.status === "in-progress" ? "bg-blue-100 text-blue-800" :
                    order.status === "completed" ? "bg-green-100 text-green-800" :
                      order.status === "scheduled" ? "bg-purple-100 text-purple-800" :
                        "bg-gray-100 text-gray-800"}
                `}
              >
                {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
              </Badge>
            </div>

            {!isInternational && (
              <div>
                <div className="text-sm text-gray-600">Location</div>
                <div className="font-medium flex items-center gap-1">
                  <MapPin className="h-4 w-4 text-gray-400" />
                  {order.location}
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-3">
            <Button className="flex items-center gap-2">
              <MessageCircle className="h-4 w-4" />
              <span className="hidden sm:inline">
                {order.unreadMessages ? `Messages (${order.unreadMessages})` : "Message"}
              </span>
              <span className="sm:hidden">
                {order.unreadMessages ? order.unreadMessages : ""}
              </span>
            </Button>

            {isInternational ? (
              <Button variant="outline" className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                <span className="hidden sm:inline">View Deliverables</span>
                <span className="sm:hidden">Deliverables</span>
              </Button>
            ) : (
              <Button variant="outline" className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <span className="hidden sm:inline">View Appointment</span>
                <span className="sm:hidden">Appointment</span>
              </Button>
            )}

            {order.status === "completed" && !order.feedback?.given && (
              <Button variant="outline" className="flex items-center gap-2">
                <Star className="h-4 w-4" />
                <span className="hidden sm:inline">Leave Review</span>
                <span className="sm:hidden">Review</span>
              </Button>
            )}
          </div>

          {/* Desktop Order Details */}
          {viewMode === "list" && (
            <div className="hidden sm:block border-t pt-4 mt-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <div className="text-sm text-gray-600">Amount</div>
                  <div className="text-lg font-semibold">
                    {order.currency} {order.amount.toLocaleString()}
                  </div>
                </div>

                <div>
                  <div className="text-sm text-gray-600">
                    {isInternational ? "Timeline" : "Appointment"}
                  </div>
                  <div className="font-medium">
                    {isInternational ? (
                      <>
                        Start: {new Date(order.startDate).toLocaleDateString()}
                        <br />
                        End: {new Date(order.endDate).toLocaleDateString()}
                      </>
                    ) : (
                      <>
                        {new Date(order.scheduledDate).toLocaleDateString()}
                        <br />
                        {order.timeSlot}
                      </>
                    )}
                  </div>
                </div>

                <div>
                  <div className="text-sm text-gray-600">Status</div>
                  <Badge variant={undefined}
                    className={`
                      ${order.status === "in-progress" ? "bg-blue-100 text-blue-800" :
                        order.status === "completed" ? "bg-green-100 text-green-800" :
                          order.status === "scheduled" ? "bg-purple-100 text-purple-800" :
                            "bg-gray-100 text-gray-800"}
                    `}
                  >
                    {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                  </Badge>
                </div>

                {!isInternational && (
                  <div>
                    <div className="text-sm text-gray-600">Location</div>
                    <div className="font-medium flex items-center gap-1">
                      <MapPin className="h-4 w-4 text-gray-400" />
                      {order.location}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </Card>
    );
  };

  // Request Quote Card Component
  const RequestQuoteCard = ({ request }) => {
    // Find the original job data for this request
    const originalJob = jobs.find(job => job.id === request.id);

    return (
      <Card className="p-3 sm:p-4 md:p-6 hover:shadow-lg transition-shadow" style={{ textAlign: "left" }}>
        <div className="flex flex-col gap-3 sm:gap-4">
          {/* Request Header */}
          <div className="flex flex-col sm:flex-row justify-between items-start gap-3">
            <div className="space-y-2 flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 break-words">
                  {request.serviceType}
                </h3>
                {request.details.isQuote ? <Badge variant={undefined} className="bg-blue-100 text-blue-800 text-xs whitespace-nowrap">
                  {t("myOrdersPage.quoteRequest")}
                </Badge> :
                  <Badge variant={undefined} className="bg-green-100 text-green-800 text-xs whitespace-nowrap">
                    {t("myOrdersPage.hiringProfessional")}
                  </Badge>}
                {request.details.isEmergency && (
                  <Badge variant="destructive" className="bg-red-100 text-red-800 text-xs whitespace-nowrap">
                    {t("myOrdersPage.emergency")}
                  </Badge>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-1.5 text-xs sm:text-sm text-gray-600">
                <span className="whitespace-nowrap">{t("myOrdersPage.request")} {request.id}</span>
                <span className="hidden sm:inline">•</span>
                <span className="whitespace-nowrap">{t("myOrdersPage.submittedOn")} {new Date(request.submittedDate).toLocaleDateString()}</span>
              </div>
              {request.details.title && (
                <div className="text-xs sm:text-sm text-gray-700 font-medium line-clamp-2 break-words">
                  {request.details.title}
                </div>
              )}
            </div>
            <Button variant="ghost" size="sm" className="self-start flex-shrink-0 h-8 w-8 p-0">
              <MoreHorizontal className="h-4 w-4 sm:h-5 sm:w-5" />
            </Button>
          </div>

          {/* Description */}
          <p className="text-sm sm:text-base text-gray-600 line-clamp-3 break-words">
            {request.description && request.description.length > 200
              ? `${request.description.substring(0, 200)}...`
              : request.description}
          </p>

          {/* Request Details for Mobile & Tablet */}
          <div className="md:hidden space-y-3 border-t pt-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-xs text-gray-600 mb-1">{t("myOrdersPage.status")}</div>
                <Badge variant={undefined}
                  className={`text-xs ${request.status === "Pending" ? "bg-yellow-100 text-yellow-800" :
                    request.status === "In Progress" ? "bg-blue-100 text-blue-800" :
                      request.status === "Completed" ? "bg-green-100 text-green-800" :
                        request.status === "Cancelled" ? "bg-red-100 text-red-800" :
                          request.status === "Inactive" ? "bg-gray-100 text-gray-800" :
                            request.status === "Active" ? "bg-green-100 text-green-800" :
                              request.status === "Approved" ? "bg-green-100 text-green-800" :
                                request.status === "Flagged" ? "bg-orange-100 text-orange-800" :
                                  "bg-gray-100 text-gray-800"}`}
                >
                  {request.status}
                </Badge>
            </div>
            <div>
                <div className="text-xs text-gray-600 mb-1">{t("myOrdersPage.budget")}</div>
                <div className="text-sm font-medium truncate">{getDynamicCurrencySymbol()}{request.details.budget?.toLocaleString() || t("myOrdersPage.notSpecified")}</div>
            </div>
          </div>
            {request.details.location && (
              <div>
                <div className="text-xs text-gray-600 mb-1">{t("myOrdersPage.location")}</div>
                <div className="text-sm font-medium flex items-center gap-1 truncate">
                  <MapPin className="h-3 w-3 sm:h-4 sm:w-4 text-gray-400 flex-shrink-0" />
                  <span className="truncate">{request.details.location}</span>
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons - Improved responsive layout */}
          <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2">
            <div className="grid grid-cols-2 gap-2 sm:contents">
              <Button 
                className="flex items-center justify-center gap-1.5 sm:gap-2 text-xs sm:text-sm h-9 sm:h-10" 
                onClick={() => {
                  window.location.href = "/client/MessageAndVideoCall";
                  // navigate("/client/MessageAndVideoCall");
                }}
              >
                <Send className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">{t("myOrdersPage.viewResponses")}</span>
                <span className="sm:hidden">{t("myOrdersPage.responses")}</span>
              </Button>
              <Button
                variant="outline"
                className="flex items-center justify-center gap-1.5 sm:gap-2 text-xs sm:text-sm h-9 sm:h-10"
                onClick={() => {
                  setSelectedJob(originalJob);
                  setShowJobDetailsModal(true);
                }}
              >
                <FileText className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">{t("myOrdersPage.viewDetails")}</span>
                <span className="sm:hidden">{t("myOrdersPage.details")}</span>
              </Button>
            </div>
            {originalJob && (
              <>
                <div className="grid grid-cols-2 gap-2 sm:contents">
                  <Button
                    variant="outline"
                    className="flex items-center justify-center gap-1.5 sm:gap-2 text-blue-600 hover:text-blue-700 text-xs sm:text-sm h-9 sm:h-10"
                    onClick={() => navigate(originalJob.isLocal ? `/clients/postajob/${originalJob.id}` : `/clients/postajob/international/${originalJob.id}`)}
                  >
                    <Edit className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                    <span className="hidden sm:inline">{t("myOrdersPage.editJob")}</span>
                    <span className="sm:hidden">{t("myOrdersPage.edit")}</span>
                  </Button>
                  {(Number(originalJob.status?.id) === JobStatusEnum.InActive || Number(originalJob.status?.id) === JobStatusEnum.Contracted) ? (
                    <Button
                      variant="outline"
                      className="flex items-center justify-center gap-1.5 sm:gap-2 text-green-600 hover:text-green-700 text-xs sm:text-sm h-9 sm:h-10"
                      onClick={() => handleActivateJob(originalJob)}
                    >
                      <Check className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                      <span className="hidden sm:inline">{t("myOrdersPage.activateJob")}</span>
                      <span className="sm:hidden">{t("myOrdersPage.activate")}</span>
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      className="flex items-center justify-center gap-1.5 sm:gap-2 text-red-600 hover:text-red-700 text-xs sm:text-sm h-9 sm:h-10"
                      onClick={() => handleDeactivateJob(originalJob)}
                    >
                      <Trash2 className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                      <span className="hidden sm:inline">{t("myOrdersPage.deactivateJob")}</span>
                      <span className="sm:hidden">{t("myOrdersPage.deactivate")}</span>
                    </Button>
                  )}
                </div>

                <Button
                  variant="outline"
                  className="flex items-center justify-center gap-1.5 sm:gap-2 text-red-600 hover:text-red-700 text-xs sm:text-sm h-9 sm:h-10 w-full sm:w-auto"
                  onClick={() => handlePermanentlyDeleteJob(originalJob)}
                >
                  <Trash2 className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  <span className="hidden sm:inline">{t("myOrdersPage.permanentlyDelete")}</span>
                  <span className="sm:hidden">{t("myOrdersPage.delete")}</span>
                </Button>
              </>
            )}
          </div>

          {/* Desktop Request Details */}
          {viewMode === "list" && (
            <div className="hidden md:block border-t pt-4">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {request.details.title && (
                  <div>
                    <div className="text-xs text-gray-600 mb-1">{t("myOrdersPage.title")}</div>
                    <div className="text-sm font-medium line-clamp-2 break-words">{request.details.title}</div>
                  </div>
                )}
                <div>
                  <div className="text-xs text-gray-600 mb-1">{t("myOrdersPage.status")}</div>
                  <Badge variant={undefined}
                    className={`text-xs ${request.status === "Pending" ? "bg-yellow-100 text-yellow-800" :
                        request.status === "In Progress" ? "bg-blue-100 text-blue-800" :
                          request.status === "Completed" ? "bg-green-100 text-green-800" :
                            request.status === "Cancelled" ? "bg-red-100 text-red-800" :
                              request.status === "Inactive" ? "bg-gray-100 text-gray-800" :
                                request.status === "Active" ? "bg-green-100 text-green-800" :
                                  request.status === "Approved" ? "bg-green-100 text-green-800" :
                                    request.status === "Flagged" ? "bg-orange-100 text-orange-800" :
                                      "bg-gray-100 text-gray-800"}`}
                  >
                    {request.status}
                  </Badge>
                </div>
                <div>
                  <div className="text-xs text-gray-600 mb-1">Budget</div>
                  <div className="text-sm font-medium">{getDynamicCurrencySymbol()}{request.details.budget?.toLocaleString() || "Not specified"}</div>
                </div>
                {request.details.location && (
                  <div>
                    <div className="text-xs text-gray-600 mb-1">{t("myOrdersPage.location")}</div>
                    <div className="text-sm font-medium flex items-center gap-1 truncate">
                      <MapPin className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      <span className="truncate">{request.details.location}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </Card>
    );
  };

  return (
    <>
      <style>{customStyles}</style>
      <div className="min-h-screen bg-gray-50 flex flex-col overflow-x-hidden" style={{ textAlign: "left" }}>
        <SharedHeader5 />

      <main className="w-full max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-4 sm:py-6 md:py-8 flex-1">
        {/* Header Section */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4 mb-4 sm:mb-6 md:mb-8">
          <div className="min-w-0 w-full sm:w-auto">
            <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 truncate">{t("myOrdersPage.title")}</h1>
            <p className="text-sm sm:text-base text-gray-600">{t("myOrdersPage.subtitle")}</p>
          </div>

        </div>

        {/* Filters and Views */}
        <div className="bg-white border rounded-lg p-3 sm:p-4 mb-4 sm:mb-6">
          <div className="flex flex-col gap-3 sm:gap-4">
            {/* Tabs */}
            <div className="flex items-center gap-2 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-hide" style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}>
              <button
                onClick={() => handleTabChange("requests")}
                className={`px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium whitespace-nowrap flex-shrink-0 ${selectedTab === "requests"
                  ? "bg-blue-50 text-blue-700"
                  : "text-gray-600 hover:bg-gray-50"
                  }`}
              >
                {t("myOrdersPage.hiringPosts")} ({countsLoading && !countsInitialized ? "..." : totalHiringPostsCount})
              </button>
              <button
                onClick={() => handleTabChange("active")}
                className={`px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium whitespace-nowrap flex-shrink-0 ${selectedTab === "active"
                  ? "bg-blue-50 text-blue-700"
                  : "text-gray-600 hover:bg-gray-50"
                  }`}
              >
                {t("myOrdersPage.activeOrders")} ({countsLoading && !countsInitialized ? "..." : activeContractsCount})
              </button>
              <button
                onClick={() => handleTabChange("past")}
                className={`px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium whitespace-nowrap flex-shrink-0 ${selectedTab === "past"
                  ? "bg-blue-50 text-blue-700"
                  : "text-gray-600 hover:bg-gray-50"
                  }`}
              >
                {t("myOrdersPage.pastOrders")} ({countsLoading && !countsInitialized ? "..." : pastOrdersCount})
              </button>
              <button
                onClick={() => handleTabChange("closed")}
                className={`px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium whitespace-nowrap flex-shrink-0 ${selectedTab === "closed"
                  ? "bg-blue-50 text-blue-700"
                  : "text-gray-600 hover:bg-gray-50"
                  }`}
              >
                {t("myOrdersPage.closedOrders")} ({countsLoading && !countsInitialized ? "..." : closedOrdersCount})
              </button>
            </div>

            {/* Search and Actions */}
            <div className="flex flex-col sm:flex-row items-stretch gap-2 sm:gap-3">
              {/* Search */}
              <div className="relative flex-1 sm:max-w-xs">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                <input
                  type="text"
                  placeholder={t("myOrdersPage.searchJobs")}
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    // Debounce search or implement search on enter/blur for backend search
                  }}
                  className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                />
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-2 flex-shrink-0">
                {/* Refresh Button */}
                <Button
                  variant="outline"
                  size="sm"
                  className="flex items-center justify-center gap-1.5 sm:gap-2 text-xs sm:text-sm h-9 sm:h-10 px-2 sm:px-3"
                  onClick={() => {
                    fetchAllCounts(true);
                    if (selectedTab === "requests") {
                      fetchQuoteRequests(1, quotePagination.limit);
                    }
                  }}
                >
                  <svg className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  <span className="hidden sm:inline">{t("myOrdersPage.refresh")}</span>
                </Button>

                {/* Filter Button */}
                <Button
                  variant="outline"
                  size="sm"
                  className="flex items-center justify-center gap-1.5 text-xs sm:text-sm h-9 sm:h-10 px-2 sm:px-3"
                  onClick={() => setFilterOpen(!filterOpen)}
                >
                  <Filter className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0" />
                  <span className="hidden sm:inline">{t("myOrdersPage.filters")}</span>
                  <ChevronDown className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0" />
                </Button>

                {/* View Toggle */}
                <div className="flex border rounded-lg overflow-hidden flex-shrink-0">
                  <button
                    onClick={() => handleViewModeChange("list")}
                    className={`p-2 ${viewMode === "list"
                      ? "bg-blue-50 text-blue-700"
                      : "bg-white text-gray-600 hover:bg-gray-50"
                      }`}
                  >
                    <List className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  </button>
                  <button
                    onClick={() => handleViewModeChange("grid")}
                    className={`p-2 ${viewMode === "grid"
                      ? "bg-blue-50 text-blue-700"
                      : "bg-white text-gray-600 hover:bg-gray-50"
                      }`}
                  >
                    <Grid className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Filter Panel */}
          {filterOpen && (
            <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3 sm:gap-4">
                {/* Order Type Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("myOrdersPage.orderType")}
                  </label>
                  <select
                    value={filters.orderType}
                    onChange={(e) => setFilters(prev => ({
                      ...prev,
                      orderType: e.target.value
                    }))}
                    className="w-full p-2 border rounded-lg"
                  >
                    <option value="all">{t("myOrdersPage.allTypes")}</option>
                    <option value="1">{t("myOrdersPage.localServices")}</option>
                    <option value="2">{t("myOrdersPage.professionalServices")}</option>
                    <option value="3">{t("myOrdersPage.internationalServices")}</option>
                  </select>
                </div>

                {/* Status Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("myOrdersPage.status")}
                  </label>
                  <select
                    value={filters.status}
                    onChange={(e) => setFilters(prev => ({
                      ...prev,
                      status: e.target.value
                    }))}
                    className="w-full p-2 border rounded-lg"
                  >
                    <option value="all">{t("myOrdersPage.allStatuses")}</option>
                    <option value="scheduled">{t("myOrdersPage.scheduled")}</option>
                    <option value="in-progress">{t("myOrdersPage.inProgress")}</option>
                    <option value="completed">{t("myOrdersPage.completed")}</option>
                    <option value="cancelled">{t("myOrdersPage.cancelled")}</option>
                    <option value="pending">{t("myOrdersPage.pendingQuotes")}</option>
                    <option value="received">{t("myOrdersPage.receivedQuotes")}</option>
                  </select>
                </div>

                {/* Category Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("myOrdersPage.category")}
                  </label>
                  <select
                    value={filters.category}
                    onChange={(e) => setFilters(prev => ({
                      ...prev,
                      category: e.target.value
                    }))}
                    className="w-full p-2 border rounded-lg"
                  >
                    <option value="all">{t("myOrdersPage.allCategories")}</option>
                    {Array.from(new Set(jobs.map(job => job.categoryId).filter(Boolean))).map(categoryObj => (
                      <option key={categoryObj.id} value={categoryObj.name}>
                        {getTranslatedCategoryName(categoryObj, currentLang) || categoryObj.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Urgency Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("myOrdersPage.urgency")}
                  </label>
                  <select
                    value={filters.urgency}
                    onChange={(e) => setFilters(prev => ({
                      ...prev,
                      urgency: e.target.value
                    }))}
                    className="w-full p-2 border rounded-lg"
                  >
                    <option value="all">{t("myOrdersPage.allUrgency")}</option>
                    <option value="emergency">{t("myOrdersPage.emergency")}</option>
                    <option value="normal">{t("myOrdersPage.normal")}</option>
                  </select>
                </div>

                {/* Timeframe Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("myOrdersPage.timeframe")}
                  </label>
                  <select
                    value={filters.timeframe}
                    onChange={(e) => setFilters(prev => ({
                      ...prev,
                      timeframe: e.target.value
                    }))}
                    className="w-full p-2 border rounded-lg"
                  >
                    <option value="all">{t("myOrdersPage.allTime")}</option>
                    <option value="today">{t("myOrdersPage.today")}</option>
                    <option value="this-week">{t("myOrdersPage.thisWeek")}</option>
                    <option value="this-month">{t("myOrdersPage.thisMonth")}</option>
                    <option value="last-3-months">{t("myOrdersPage.last3Months")}</option>
                    <option value="last-6-months">{t("myOrdersPage.last6Months")}</option>
                  </select>
                </div>
              </div>

              {/* Filter Actions */}
              <div className="flex flex-col sm:flex-row justify-end gap-2 mt-4">
                <Button
                  variant="outline"
                  className="w-full sm:w-auto"
                  onClick={() => {
                    setFilters({
                      orderType: "all",
                      status: "all",
                      timeframe: "all",
                      category: "all",
                      urgency: "all"
                    });
                    setSearchQuery("");
                    if (selectedTab === "requests") {
                      fetchQuoteRequests(1, quotePagination.limit);
                    }
                    // Refresh total counts
                    fetchAllCounts(true);
                  }}
                >
                  {t("myOrdersPage.clearFilters")}
                </Button>
                <Button
                  className="w-full sm:w-auto"
                  onClick={() => {
                    if (selectedTab === "requests") {
                      // Build additional filters based on UI state
                      const additionalFilters: any = {};

                      // Add category filter if not "all"
                      if (filters.category !== "all") {
                        additionalFilters.category = filters.category;
                      }

                      // Add urgency filter if not "all"
                      if (filters.urgency !== "all") {
                        additionalFilters.isEmergency = filters.urgency === "emergency";
                      }

                      fetchQuoteRequests(1, quotePagination.limit, additionalFilters);
                    }
                    // Refresh total counts
                    fetchAllCounts(true);
                  }}
                >
                  {t("myOrdersPage.applyFilters")}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Orders Grid/List */}
        <div className={
          viewMode === "grid"
            ? "grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 lg:gap-6"
            : "space-y-3 sm:space-y-4 lg:space-y-6"
        }>
          {selectedTab === "active" ? (
            <ActiveContractsTab 
              onCountChange={(c) => {
                // Only update if counts are not currently loading to prevent flickering
                if (!countsLoading) {
                  setActiveContractsCount(Number(c) || 0);
                }
              }}
              statusFilter={[ContractStatusEnum.Active, ContractStatusEnum.Accepted]}
              onContractCompleted={() => {
                // Switch to Past Orders tab after completion
                handleTabChange("past");
                // Refresh counts after switching
                fetchAllCounts(true);
              }}
            />
          ) : selectedTab === "past" ? (
            <ActiveContractsTab 
              onCountChange={handlePastOrdersCountChange}
              statusFilter={[ContractStatusEnum.Completed]}
              onContractCompleted={handleContractCompleted}
            />
          ) : selectedTab === "closed" ? (
            <ActiveContractsTab 
              onCountChange={handleClosedOrdersCountChange}
              statusFilter={[ContractStatusEnum.Closed]}
              onContractCompleted={handleContractCompleted}
            />
          ) : (
            loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="flex flex-col items-center gap-4">
                  <Loader className="h-8 w-8 animate-spin text-blue-600" />
                  <p className="text-gray-600">{t("myOrdersPage.loadingQuoteRequests")}</p>
                </div>
              </div>
            ) : requestQuotes.length > 0 ? (
              requestQuotes.map(request => (
                <RequestQuoteCard key={request.id} request={request} />
              ))
            ) : (
              <div className="text-center py-8 sm:py-12">
                <div className="mb-4">
                  <FileText className="h-12 w-12 text-gray-400 mx-auto" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {t("myOrdersPage.noQuoteRequests")}
                </h3>
                <p className="text-gray-600">
                  {t("myOrdersPage.noQuoteRequestsMessage")}
                </p>
              </div>
            )
          )}
        </div>

        {/* Pagination */}
        {selectedTab === "requests" && requestQuotes.length > 0 && (
          <div className="mt-4 sm:mt-6 lg:mt-8">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-3 sm:gap-4">
              {/* Page Info */}
              <div className="text-xs sm:text-sm text-gray-600 text-center sm:text-left">
                Showing {((quotePagination.currentPage - 1) * quotePagination.limit) + 1} to{" "}
                {Math.min(quotePagination.currentPage * quotePagination.limit, quotePagination.totalCount)} of{" "}
                {quotePagination.totalCount} quote requests
                {quotePagination.totalPages > 1 && (
                  <span className="hidden sm:inline"> (Page {quotePagination.currentPage} of {quotePagination.totalPages})</span>
                )}
              </div>

              {/* Pagination Controls */}
              <nav className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                <Button
                  variant="outline"
                  size="sm"
                  className="w-16 sm:w-20 text-xs sm:text-sm h-8 sm:h-9"
                  disabled={quotePagination.currentPage === 1}
                  onClick={() => handleQuotePageChange(quotePagination.currentPage - 1)}
                >
                  Previous
                </Button>

                {/* Page Numbers */}
                <div className="flex items-center gap-0.5 sm:gap-1">
                  {(() => {
                    const pages = [];
                    const totalPages = quotePagination.totalPages;
                    const currentPage = quotePagination.currentPage;

                    // Show first page
                    if (totalPages > 0) {
                      pages.push(
                        <Button
                          key={1}
                          size="sm"
                          variant={currentPage === 1 ? "default" : "ghost"}
                          className="w-7 h-7 sm:w-9 sm:h-9 text-xs sm:text-sm p-0 min-w-0"
                          onClick={() => handleQuotePageChange(1)}
                        >
                          1
                        </Button>
                      );
                    }

                    // Show ellipsis if needed
                    if (currentPage > 4) {
                      pages.push(
                        <span key="ellipsis1" className="px-1 sm:px-2 text-gray-500 text-xs sm:text-sm">...</span>
                      );
                    }

                    // Show pages around current page
                    for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
                      if (i > 1 && i < totalPages) {
                        pages.push(
                          <Button
                            key={i}
                            size="sm"
                            variant={currentPage === i ? "default" : "ghost"}
                            className="w-7 h-7 sm:w-9 sm:h-9 text-xs sm:text-sm p-0 min-w-0"
                            onClick={() => handleQuotePageChange(i)}
                          >
                            {i}
                          </Button>
                        );
                      }
                    }

                    // Show ellipsis if needed
                    if (currentPage < totalPages - 3) {
                      pages.push(
                        <span key="ellipsis2" className="px-1 sm:px-2 text-gray-500 text-xs sm:text-sm">...</span>
                      );
                    }

                    // Show last page
                    if (totalPages > 1) {
                      pages.push(
                        <Button
                          key={totalPages}
                          size="sm"
                          variant={currentPage === totalPages ? "default" : "ghost"}
                          className="w-7 h-7 sm:w-9 sm:h-9 text-xs sm:text-sm p-0 min-w-0"
                          onClick={() => handleQuotePageChange(totalPages)}
                        >
                          {totalPages}
                        </Button>
                      );
                    }

                    return pages;
                  })()}
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  className="w-16 sm:w-20 text-xs sm:text-sm h-8 sm:h-9"
                  disabled={!quotePagination.hasNextPage}
                  onClick={() => handleQuotePageChange(quotePagination.currentPage + 1)}
                >
                  Next
                </Button>
              </nav>
            </div>
          </div>
        )}

      </main>

      {/* Footer */}
      <footer className="bg-white border-t w-full mt-auto">
        <div className="max-w-7xl mx-auto">
          {!loading && <SharedFooter3 />}
        </div>
      </footer>

      {/* Job Details Modal */}
      {showJobDetailsModal && selectedJob && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-4">
              <h2 className="text-xl font-semibold">{t("myOrdersPage.jobDetailsModal.title")}</h2>
              <button
                onClick={() => setShowJobDetailsModal(false)}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Job Header */}
              <div className="border-b pb-4">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {selectedJob.title || (selectedJob.categoryId ? (getTranslatedCategoryName(selectedJob.categoryId, currentLang) || selectedJob.categoryId.name) : t("myOrdersPage.jobDetailsModal.noTitleFallback"))}
                  </h3>
                  <Badge variant={undefined} className="bg-blue-100 text-blue-800">
                    {selectedJob.isQuote ? t("myOrdersPage.quoteRequest") : t("myOrdersPage.jobDetailsModal.directHireBadge")}
                  </Badge>
                  {selectedJob.isEmergency && (
                    <Badge variant="destructive" className="bg-red-100 text-red-800">
                      {t("myOrdersPage.jobDetailsModal.emergencyBadge")}
                    </Badge>
                  )}
                  <Badge variant={undefined} className={`
                    ${Number(selectedJob.status?.id) === JobStatusEnum.Pending ? "bg-yellow-100 text-yellow-800" :
                      Number(selectedJob.status?.id) === JobStatusEnum.InProgress ? "bg-blue-100 text-blue-800" :
                        Number(selectedJob.status?.id) === JobStatusEnum.Completed ? "bg-green-100 text-green-800" :
                          Number(selectedJob.status?.id) === JobStatusEnum.Cancelled ? "bg-red-100 text-red-800" :
                            Number(selectedJob.status?.id) === JobStatusEnum.InActive ? "bg-gray-100 text-gray-800" :
                              Number(selectedJob.status?.id) === JobStatusEnum.Active ? "bg-green-100 text-green-800" :
                                Number(selectedJob.status?.id) === JobStatusEnum.Approved ? "bg-green-100 text-green-800" :
                                  Number(selectedJob.status?.id) === JobStatusEnum.Flagged ? "bg-orange-100 text-orange-800" :
                                    Number(selectedJob.status?.id) === JobStatusEnum.Deleted ? "bg-red-100 text-red-800" :
                                      Number(selectedJob.status?.id) === JobStatusEnum.Contracted ? "bg-blue-100 text-blue-800" :
                                        "bg-gray-100 text-gray-800"}
                  `}>
                    {getJobStatusName(selectedJob.status?.id)}
                  </Badge>
                </div>
                {selectedJob.title && selectedJob.categoryId?.name && (
                  <p className="text-sm text-gray-600 mb-1">
                    {t("myOrdersPage.jobDetailsModal.categoryLabel")} {getTranslatedCategoryName(selectedJob.categoryId, currentLang) || selectedJob.categoryId.name}
                  </p>
                )}
                <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                  <span>{t("myOrdersPage.jobDetailsModal.submittedOn", { date: new Date(selectedJob.createdAt).toLocaleDateString() })}</span>
                  {selectedJob.updatedAt && (
                    <span>{t("myOrdersPage.jobDetailsModal.lastUpdated", { date: new Date(selectedJob.updatedAt).toLocaleDateString() })}</span>
                  )}
                  {selectedJob.isQuote && (
                    <span>{t("myOrdersPage.jobDetailsModal.quoteRequestInfo")}</span>
                  )}
                  {selectedJob.isEmergency && (
                    <span>{t("myOrdersPage.jobDetailsModal.emergencyServiceInfo")}</span>
                  )}
                </div>
              </div>

              {/* Description */}
              <div>
                <h4 className="font-medium text-gray-900 mb-2">{t("myOrdersPage.jobDetailsModal.description")}</h4>
                <p className="text-gray-700 whitespace-pre-wrap line-clamp-4 sm:line-clamp-none">
                  {selectedJob.description && selectedJob.description.length > 200
                    ? `${selectedJob.description.substring(0, 200)}...`
                    : selectedJob.description || t("myOrdersPage.jobDetailsModal.noDescription")}
                </p>
              </div>

              {/* Job Details Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Service Information */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">{t("myOrdersPage.jobDetailsModal.serviceInformation")}</h4>
                  <div className="space-y-3">
                    <div>
                      <span className="text-sm text-gray-600">{t("myOrdersPage.jobDetailsModal.categoryLabel")}</span>
                      <p className="font-medium">{selectedJob.categoryId ? (getTranslatedCategoryName(selectedJob.categoryId, currentLang) || selectedJob.categoryId.name) : t("myOrdersPage.notSpecified")}</p>
                    </div>
                    {Number(selectedJob.categoryId.type.id) === TypeEnum.Home_Service && <><div>
                      <span className="text-sm text-gray-600">{t("myOrdersPage.jobDetailsModal.serviceType")}</span>
                      <p className="font-medium">{getServiceTypeName(selectedJob.serviceType?.id)}</p>
                    </div><div>
                        <span className="text-sm text-gray-600">{t("myOrdersPage.jobDetailsModal.propertyType")}</span>
                        <p className="font-medium">{getPropertyTypeName(selectedJob.propertyType?.id)}</p>
                      </div></>}
                    <div>
                      <span className="text-sm text-gray-600">{t("myOrdersPage.jobDetailsModal.budgetType")}</span>
                      <p className="font-medium">{getBudgetTypeName(selectedJob.budgetType?.id)}</p>
                    </div>
                  </div>
                </div>

                {/* Budget & Timeline */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">{t("myOrdersPage.jobDetailsModal.budgetTimeline")}</h4>
                  <div className="space-y-3">
                    <div>
                      <span className="text-sm text-gray-600">{t("myOrdersPage.jobDetailsModal.budgetLabel")}</span>
                      <p className="font-medium">
                        {selectedJob.budget ? `${getDynamicCurrencySymbol()}${selectedJob.budget.toLocaleString()}` : t("myOrdersPage.notSpecified")}
                      </p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">{t("myOrdersPage.jobDetailsModal.currencyLabel")}</span>
                      <p className="font-medium">{selectedJob.currency?.name || t("myOrdersPage.notSpecified")}</p>
                    </div>
                    {Number(selectedJob.categoryId.type.id) === TypeEnum.Home_Service && <div>
                      <span className="text-sm text-gray-600">{t("myOrdersPage.jobDetailsModal.deadlineType")}</span>
                      <p className="font-medium">{getDeadlineTypeName(selectedJob.deadlineType?.id)}</p>
                    </div>}
                  </div>
                </div>
              </div>

              {/* Location Information */}
              {Number(selectedJob.categoryId.type.id) === TypeEnum.Home_Service && <div>
                <h4 className="font-medium text-gray-900 mb-3">{t("myOrdersPage.jobDetailsModal.location")}</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <span className="text-sm text-gray-600">{t("myOrdersPage.jobDetailsModal.address")}</span>
                    <p className="font-medium">{selectedJob.address || t("myOrdersPage.notSpecified")}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600">{t("myOrdersPage.jobDetailsModal.street")}</span>
                    <p className="font-medium">{selectedJob.street || t("myOrdersPage.notSpecified")}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600">{t("myOrdersPage.jobDetailsModal.city")}</span>
                    <p className="font-medium">{selectedJob.city || t("myOrdersPage.notSpecified")}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600">{t("myOrdersPage.jobDetailsModal.municipality")}</span>
                    <p className="font-medium">{selectedJob.municipality || selectedJob.city || t("myOrdersPage.notSpecified")}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600">{t("myOrdersPage.jobDetailsModal.province")}</span>
                    <p className="font-medium">{selectedJob.province || selectedJob.state || t("myOrdersPage.notSpecified")}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600">{t("myOrdersPage.jobDetailsModal.postalCode")}</span>
                    <p className="font-medium">{selectedJob.postalCode || selectedJob.zipCode || t("myOrdersPage.notSpecified")}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600">{t("myOrdersPage.jobDetailsModal.floor")}</span>
                    <p className="font-medium">
                      {selectedJob.floor && selectedJob.floor > 0
                        ? t("myOrdersPage.jobDetailsModal.floorValue", { floor: selectedJob.floor, suffix: getFloorSuffix(selectedJob.floor) })
                        : t("myOrdersPage.jobDetailsModal.groundFloor")}
                    </p>
                  </div>
                </div>
              </div>}

              {/* Contact Information */}
              <div>
                <h4 className="font-medium text-gray-900 mb-3">{t("myOrdersPage.jobDetailsModal.contactInformation")}</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <span className="text-sm text-gray-600">{t("myOrdersPage.jobDetailsModal.name")}</span>
                    <p className="font-medium">{selectedJob.contactName || t("myOrdersPage.notSpecified")}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600">{t("myOrdersPage.jobDetailsModal.phone")}</span>
                    <p className="font-medium">{selectedJob.contactPhone || t("myOrdersPage.notSpecified")}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600">{t("myOrdersPage.jobDetailsModal.email")}</span>
                    <p className="font-medium">{selectedJob.contactEmail || t("myOrdersPage.notSpecified")}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600">{t("myOrdersPage.jobDetailsModal.whatsapp")}</span>
                    <p className="font-medium">{selectedJob.contactWhatsapp || t("myOrdersPage.notSpecified")}</p>
                  </div>
                </div>
              </div>

              {/* Emergency Information */}
              {selectedJob.isEmergency && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">{t("myOrdersPage.jobDetailsModal.emergencyInformation")}</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <span className="text-sm text-gray-600">{t("myOrdersPage.jobDetailsModal.emergencyWhatsapp")}</span>
                      <p className="font-medium">{selectedJob.emergencyWhatsappNumber || t("myOrdersPage.notSpecified")}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">{t("myOrdersPage.jobDetailsModal.serviceArea")}</span>
                      <p className="font-medium">{selectedJob.serviceArea?.name || t("myOrdersPage.notSpecified")}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Equipment Information - Show when service type is repair */}
              {(Number(selectedJob.serviceType?.id) === 2 || Number(selectedJob.serviceType?.id) === 3) && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">{t("myOrdersPage.jobDetailsModal.equipmentInformation")}</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <span className="text-sm text-gray-600">{t("myOrdersPage.jobDetailsModal.equipmentType")}</span>
                      <p className="font-medium">{selectedJob.equipmentTypeVal || selectedJob.equipmentType || t("myOrdersPage.notSpecified")}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">{t("myOrdersPage.jobDetailsModal.brand")}</span>
                      <p className="font-medium">{selectedJob.equipmentBrand || t("myOrdersPage.notSpecified")}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">{t("myOrdersPage.jobDetailsModal.model")}</span>
                      <p className="font-medium">{selectedJob.equipmentModel || t("myOrdersPage.notSpecified")}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">{t("myOrdersPage.jobDetailsModal.year")}</span>
                      <p className="font-medium">{selectedJob.equipmentYear || t("myOrdersPage.notSpecified")}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Lawyer Job Requirements - Show when isQuote is false */}
              {Number(selectedJob.categoryId.type.id) != TypeEnum.Home_Service && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">{t("myOrdersPage.jobDetailsModal.jobRequirements")}</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {selectedJob.languages && selectedJob.languages.length > 0 && (
                      <div>
                        <span className="text-sm text-gray-600">{t("myOrdersPage.jobDetailsModal.requiredLanguages")}</span>
                        <p className="font-medium">
                          {selectedJob.languages.map((lang: any, index: number) => (
                            <span key={lang.id || index}>
                              {lang.name || lang.initial || t("myOrdersPage.jobDetailsModal.languageFallback", { index: index + 1 })}
                              {index < selectedJob.languages.length - 1 ? ", " : ""}
                            </span>
                          ))}
                        </p>
                      </div>
                    )}
                    {selectedJob.requiredExperience && (
                      <div>
                        <span className="text-sm text-gray-600">{t("myOrdersPage.jobDetailsModal.requiredExperience")}</span>
                        <p className="font-medium">
                          {Number(selectedJob.requiredExperience?.id) === 1 ? t("myOrdersPage.jobDetailsModal.entryLevel") :
                            Number(selectedJob.requiredExperience?.id) === 2 ? t("myOrdersPage.jobDetailsModal.intermediate") :
                              Number(selectedJob.requiredExperience?.id) === 3 ? t("myOrdersPage.jobDetailsModal.expert") :
                                Number(selectedJob.requiredExperience?.id) === 4 ? t("myOrdersPage.jobDetailsModal.specialist") :
                                  t("myOrdersPage.notSpecified")}
                        </p>
                      </div>
                    )}
                    {selectedJob.durationTimeline && (
                      <div>
                        <span className="text-sm text-gray-600">{t("myOrdersPage.jobDetailsModal.projectDuration")}</span>
                        <p className="font-medium">
                          {Number(selectedJob.durationTimeline?.id) === 1 ? t("myOrdersPage.jobDetailsModal.lessThanOneMonth") :
                            Number(selectedJob.durationTimeline?.id) === 2 ? t("myOrdersPage.jobDetailsModal.oneToThreeMonths") :
                              Number(selectedJob.durationTimeline?.id) === 3 ? t("myOrdersPage.jobDetailsModal.threeToSixMonths") :
                                Number(selectedJob.durationTimeline?.id) === 4 ? t("myOrdersPage.jobDetailsModal.moreThanSixMonths") :
                                  t("myOrdersPage.notSpecified")}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 mt-6 pt-4 border-t">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setShowJobDetailsModal(false)}
              >
                {t("myOrdersPage.jobDetailsModal.close")}
              </Button>
              <Button
                className="flex-1"
                onClick={() => {
                  setShowJobDetailsModal(false);
                  navigate(`/clients/postajob/${selectedJob.id}`);
                }}
              >
                {t("myOrdersPage.editJob")}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Deactivate Confirmation Modal */}
      {showDeleteModal && selectedJob && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-lg font-semibold mb-4">{t("myOrdersPage.deactivateModal.title")}</h2>
            <p className="text-gray-600 mb-6">
              {t("myOrdersPage.deactivateModal.description", {
                title: selectedJob.title || t("myOrdersPage.jobDetailsModal.noTitleFallback")
              })}
            </p>
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setShowDeleteModal(false)}
              >
                {t("myOrdersPage.deactivateModal.cancel")}
              </Button>
              <Button
                variant="destructive"
                className="flex-1"
                onClick={() => handleConfirmDelete(JobStatusEnum.InActive)}
              >
                {t("myOrdersPage.deactivateModal.deactivate")}
              </Button>
              <Button
                variant="destructive"
                className="flex-1"
                onClick={() => handleConfirmDelete(JobStatusEnum.Deleted)}
              >
                {t("myOrdersPage.deactivateModal.permanentlyDelete")}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Professional Invite Modal */}
      {showProfessionalInviteModal && <ProfessionalInviteModal
        isOpen={showProfessionalInviteModal}
        onClose={() => setShowProfessionalInviteModal(false)}
        onLocalProfessionalClick={() => {
          setShowProfessionalInviteModal(false);
          navigate("/local-professionals");
        }}
        onInternationalProfessionalClick={() => {
          setShowProfessionalInviteModal(false);
          navigate("/professional-international");
        }}
      />}
      </div>
    </>
  );
};

export default MyOrdersPage;